import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@/components/ui/form';
import { useNotificationsProvider } from '../../../context/NotificationsContext';
import {
  getNotificationTypes,
  translate,
} from '../../../entities/notification-type';
import { useManageNotificationsMutation } from '../../../services/api';

ManageUserNotificationsDialog.propTypes = {
  user: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};

export default function ManageUserNotificationsDialog({
  user,
  open,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const [manageNotifications, { isLoading }] = useManageNotificationsMutation();

  const schema = z.object({
    notifications: z.array(z.string()),
  });

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { notifications: user?.notifications || [] },
  });

  // Reset whenever the prop user changes (e.g. opening the dialog
  // against a different account) and on every open.
  useEffect(() => {
    form.reset({ notifications: user?.notifications || [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, open]);

  const onOpenChange = (next) => {
    if (!next) {
      if (isLoading) return;
      form.reset({ notifications: user?.notifications || [] });
      onClose?.();
    }
  };

  const onSubmit = async (values) => {
    try {
      await manageNotifications({
        id: 'me',
        notifications: values.notifications,
      }).unwrap();
      onConfirm?.();
      showNotification('success', t('user.manage-notifications-dialog.success'));
    } catch (err) {
      const errs = err?.data?.validationErrors;
      if (errs && errs.length > 0) {
        showNotification('error', errs[0].errorMessage);
      } else {
        showNotification('error', t('errors.api-error'));
      }
    }
  };

  const types = getNotificationTypes(user?.role) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => { if (isLoading) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isLoading) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{t('user.manage-notifications-dialog.title')}</DialogTitle>
          <DialogDescription>
            {t('user.manage-notifications-dialog.info-message')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-3"
            noValidate
          >
            <p className="text-sm">{t('user.manage-notifications-dialog.message')}</p>
            <FormField
              control={form.control}
              name="notifications"
              render={({ field }) => (
                <FormItem>
                  <div className="space-y-2">
                    {types.map((notificationType) => {
                      const checked = field.value.includes(notificationType);
                      return (
                        <label
                          key={notificationType}
                          className="flex items-center gap-2 text-sm"
                        >
                          <FormControl>
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => {
                                if (v) {
                                  field.onChange([...field.value, notificationType]);
                                } else {
                                  field.onChange(
                                    field.value.filter((n) => n !== notificationType),
                                  );
                                }
                              }}
                            />
                          </FormControl>
                          <span>{translate(notificationType, t)}</span>
                        </label>
                      );
                    })}
                  </div>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
